import React, { useEffect, useState, useCallback } from 'react';
import {
  Mail, Bell, CheckCircle, ShieldCheck,
  Save, RotateCcw, Eye, Code2, ChevronRight,
  Sparkles,
} from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const TYPES = [
  {
    key: 'signing_invitation',
    label: 'Signing Invitation',
    icon: Mail,
    desc: 'Sent when a document is dispatched for signature',
    vars: ['recipient_name', 'sender_name', 'document_title', 'message', 'signing_url'],
  },
  {
    key: 'reminder',
    label: 'Reminder',
    icon: Bell,
    desc: 'Sent when you remind pending signers',
    vars: ['recipient_name', 'document_title', 'signing_url'],
  },
  {
    key: 'completion',
    label: 'Completion Notice',
    icon: CheckCircle,
    desc: 'Sent to all parties when signing is complete',
    vars: ['recipient_name', 'document_title', 'verify_url'],
  },
  {
    key: 'otp_verification',
    label: 'OTP Verification',
    icon: ShieldCheck,
    desc: 'Sent with the one-time code before signing',
    vars: ['recipient_name', 'otp_code'],
  },
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState({});   // {type: {subject, body, is_custom}}
  const [selected, setSelected]   = useState('signing_invitation');
  const [form, setForm]           = useState({ subject: '', body: '' });
  const [tab, setTab]             = useState('edit');   // 'edit' | 'preview'
  const [preview, setPreview]     = useState({ subject: '', html: '' });
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    API.get('/email-templates')
      .then(r => {
        const map = {};
        r.data.forEach(t => { map[t.type] = t; });
        setTemplates(map);
        const cur = map[selected];
        if (cur) setForm({ subject: cur.subject, body: cur.body });
      })
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, [selected]);

  useEffect(() => { load(); }, []);

  // When user switches template type, sync the form
  const selectType = (key) => {
    setSelected(key);
    setTab('edit');
    const cur = templates[key];
    if (cur) setForm({ subject: cur.subject, body: cur.body });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/email-templates/${selected}`, form);
      toast.success('Template saved');
      // Refresh
      const r = await API.get('/email-templates');
      const map = {};
      r.data.forEach(t => { map[t.type] = t; });
      setTemplates(map);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this template to the default? Your changes will be lost.')) return;
    try {
      await API.delete(`/email-templates/${selected}`);
      toast.success('Reset to default');
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await API.post(`/email-templates/${selected}/preview`, form);
      setPreview(r.data);
      setTab('preview');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const insertVar = (varName) => {
    setForm(p => ({ ...p, body: p.body + `{{${varName}}}` }));
  };

  const currentType = TYPES.find(t => t.key === selected);
  const currentTmpl = templates[selected];

  if (loading) return (
    <div className="p-8 flex justify-center"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>
  );

  return (
    <div className="flex h-full fade-in" style={{ minHeight: 'calc(100vh - 56px)' }}>

      {/* ── Left: template type list ── */}
      <aside className="w-64 flex-shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="px-4 py-5 border-b border-slate-100">
          <h1 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Email Templates</h1>
          <p className="text-xs text-slate-400 mt-0.5">Customize outgoing notifications</p>
        </div>

        <nav className="p-2 space-y-1">
          {TYPES.map(({ key, label, icon: Icon, desc }) => {
            const tmpl = templates[key];
            const isActive = selected === key;
            return (
              <button
                key={key}
                onClick={() => selectType(key)}
                className={`w-full text-left px-3 py-3 rounded-sm transition-colors group ${
                  isActive ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
                }`}
                data-testid={`template-type-${key}`}
              >
                <div className="flex items-center gap-2.5 mb-1">
                  <Icon size={14} className={isActive ? 'text-blue-700' : 'text-slate-400'} />
                  <span className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-slate-700'}`}>{label}</span>
                  {tmpl?.is_custom && (
                    <span className="ml-auto text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-sm font-medium">Custom</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 leading-snug pl-[22px]">{desc}</p>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Right: editor ── */}
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">

        {/* Editor header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            {currentType && <currentType.icon size={16} className="text-blue-700" />}
            <h2 className="text-sm font-semibold text-slate-900">{currentType?.label}</h2>
            {currentTmpl?.is_custom && (
              <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-sm">Custom</span>
            )}
            {!currentTmpl?.is_custom && (
              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-sm">Default</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {currentTmpl?.is_custom && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-200 px-3 py-1.5 rounded-sm transition-colors"
                data-testid="reset-template-btn"
              >
                <RotateCcw size={12} /> Reset to Default
              </button>
            )}
            <button
              onClick={handlePreview} disabled={loadingPreview}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-700 border border-slate-200 hover:border-blue-300 px-3 py-1.5 rounded-sm transition-colors"
              data-testid="preview-template-btn"
            >
              <Eye size={12} /> {loadingPreview ? 'Loading...' : 'Preview'}
            </button>
            <button
              onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white px-4 py-1.5 rounded-sm text-xs font-medium transition-colors disabled:opacity-60"
              data-testid="save-template-btn"
            >
              <Save size={12} /> {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-slate-200 px-6 flex gap-1 flex-shrink-0">
          {[{ key: 'edit', label: 'Edit', icon: Code2 }, { key: 'preview', label: 'Preview', icon: Eye }].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => key === 'preview' ? handlePreview() : setTab('edit')}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                tab === key ? 'border-blue-700 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {/* Edit tab */}
        {tab === 'edit' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* Subject */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Subject Line</label>
              <input
                type="text" value={form.subject}
                onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Email subject…"
                data-testid="template-subject-input"
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Body (HTML)</label>
              <textarea
                value={form.body}
                onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                rows={18}
                spellCheck={false}
                className="w-full px-3 py-3 border border-slate-300 rounded-sm text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.6 }}
                placeholder="<h2>Hello {{recipient_name}},</h2>…"
                data-testid="template-body-input"
              />
            </div>

            {/* Variables */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles size={12} className="text-blue-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Available Variables</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">Click to insert at end of body. Wrap in <code className="bg-slate-100 px-1 rounded text-slate-600">{`{{`}…{`}}`}</code> syntax.</p>
              <div className="flex flex-wrap gap-2">
                {currentType?.vars.map(v => (
                  <button
                    key={v}
                    onClick={() => insertVar(v)}
                    className="font-mono text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-sm hover:bg-blue-100 transition-colors"
                    data-testid={`var-chip-${v}`}
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Tip */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-700">
              The email header (SignatureFlow logo + brand color) and footer are automatically wrapped around your body HTML.
            </div>
          </div>
        )}

        {/* Preview tab */}
        {tab === 'preview' && (
          <div className="flex-1 overflow-y-auto p-6">
            {preview.html ? (
              <div>
                <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 mb-4 flex items-center gap-3">
                  <span className="text-xs text-slate-500 font-medium">Subject:</span>
                  <span className="text-sm text-slate-800 font-medium">{preview.subject}</span>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
                  <iframe
                    srcDoc={preview.html}
                    title="Email Preview"
                    className="w-full"
                    style={{ height: 520, border: 'none' }}
                    sandbox="allow-same-origin"
                    data-testid="email-preview-iframe"
                  />
                </div>
                <p className="text-xs text-slate-400 text-center mt-3">Preview uses sample data. Actual values will be substituted when sent.</p>
              </div>
            ) : (
              <div className="text-center py-20 text-slate-400">
                <Eye size={36} className="mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Click "Preview" to render the template with sample data.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
